import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Checkbox, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";
import AppButton from "../../../common_components/AppButton";
import CommonLoader from "../../../common_components/CommonLoader";
import CustomLoader from "../../../common_components/CustomLoader";
import { StyledCurrency } from "../../../helper/StyledCurrency";
import { formatStat } from "../../../helper/statsFormatter";
import InvoicePreviewModal from "./InvoicePreviewModal";
/** Same geometry as the module's other modal buttons — see `GenerateInvoiceModal`'s note
 *  on why `color="inherit"` is required to escape the global `containedPrimary` rule. */
const BUTTON_SX = {
    minWidth: 110,
    height: 36,
    minHeight: 36,
    px: "14px",
    borderRadius: "8px",
    textTransform: "capitalize",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap",
    boxShadow: "0px 1px 2px 0px #0A0D120D",
};
/**
 * Column track shared by the header and every row, so the five columns line up without a
 * real table — the same approach `AdjustTotalBillModal` takes. Collapses to three columns
 * on narrow screens, dropping Invoice Date and Client Name rather than the tick or the
 * preview.
 */
const ROW_GRID = {
    display: "grid",
    gridTemplateColumns: { xs: "44px 1fr 130px", sm: "44px 150px 1.4fr 1fr 130px" },
    gap: "12px",
    alignItems: "center",
};
/** Cells hidden at the `xs` breakpoint, where the grid drops to three columns. */
const SM_ONLY = { display: { xs: "none", sm: "block" } };
const CHECKBOX_SX = {
    p: 0,
    color: "#D0D5DD",
    "&.Mui-checked": { color: "#FF008E" },
    "&.MuiCheckbox-indeterminate": { color: "#FF008E" },
};
/**
 * "Mapping Invoice" — the ADDITIONAL QuickBooks invoices a client holds for the billing
 * month, and which of them are mapped onto its billing row.
 *
 * Opened from the listing's "Mapping Invoice" action, which only appears while a checked
 * row is flagged `has_multiple_invoice` by the last refresh. The invoice the row already
 * records is excluded server-side — everything listed here is an extra.
 *
 * View-only apart from the ticks: the preview shows the PDF alone, with no Internal Status
 * or Notes, because those are stored against the row's recorded invoice rather than these.
 *
 * The ticks ARE the state. They open on what's stored, and Save submits the whole list, so
 * unticking a previously mapped invoice removes its mapping.
 */
const MappingInvoiceModal = ({ open, clientIds, onLoad, onSubmit, onClose, loading = false, }) => {
    const [entries, setEntries] = useState([]);
    const [fetching, setFetching] = useState(false);
    /**
     * Failures are shown in the dialog rather than as a toast: it sits at `zIndex: 2000`,
     * above `ToastProvider`'s snackbar layer, so a toast raised while it's open would render
     * behind it. Same reasoning as the Adjust Total Bill modal.
     */
    const [error, setError] = useState(null);
    /** The invoice whose PDF is open, or null. Held whole so the preview gets its header. */
    const [previewEntry, setPreviewEntry] = useState(null);
    // Re-read on every open: the invoices come from QuickBooks and the ticks from our
    // database, so a stale list could offer an invoice that has since been deleted there.
    // `clientIds` is joined into the dependency so a changed selection re-reads too.
    const clientKey = clientIds.join(",");
    useEffect(() => {
        if (!open || !clientKey)
            return;
        let cancelled = false;
        setFetching(true);
        setError(null);
        setEntries([]);
        (async () => {
            try {
                const loaded = await onLoad(clientKey.split(","));
                if (!cancelled)
                    setEntries(loaded);
            }
            catch (err) {
                if (!cancelled) {
                    setError(typeof err === "string"
                        ? err
                        : err?.message || "Couldn't read the invoices from QuickBooks. Please try again.");
                }
            }
            finally {
                if (!cancelled)
                    setFetching(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, clientKey, onLoad]);
    const toggleEntry = useCallback((invoiceId) => {
        setEntries((prev) => prev.map((entry) => entry.invoiceId === invoiceId ? { ...entry, selected: !entry.selected } : entry));
        // Re-ticking is the retry, so a previous failure notice clears as soon as the user acts.
        setError(null);
    }, []);
    const selectedCount = useMemo(() => entries.filter((entry) => entry.selected).length, [entries]);
    const allSelected = entries.length > 0 && selectedCount === entries.length;
    // Select-all clears rather than inverts when everything is already ticked, matching the
    // listing's own header checkbox.
    const toggleAll = useCallback(() => {
        setEntries((prev) => {
            const next = !prev.every((entry) => entry.selected);
            return prev.map((entry) => ({ ...entry, selected: next }));
        });
        setError(null);
    }, []);
    const handleSubmit = useCallback(async () => {
        setError(null);
        const saved = await onSubmit(entries);
        if (saved === false) {
            setError("Couldn't save the invoice mapping. Please try again.");
        }
    }, [entries, onSubmit]);
    if (!open)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(Dialog, { open: open, maxWidth: "md", fullWidth: true, 
                // Same rule as the module's other dialogs: only the explicit controls dismiss
                // it, so a stray backdrop click or Esc can't discard the selection.
                disableEscapeKeyDown: true, sx: {
                    zIndex: 2000,
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.5)",
                        backdropFilter: "blur(3px)",
                    },
                    "& .MuiDialog-container > .MuiPaper-root": { borderRadius: "12px", maxWidth: "860px" },
                }, children: _jsxs(DialogContent, { sx: { p: 0 }, children: [_jsxs(Box, { sx: {
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 2,
                                px: 3,
                                py: 2,
                                borderBottom: "1px solid #EAECF0",
                            }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: "18px", fontWeight: 600, color: "#101828", lineHeight: "28px" }, children: "Mapping Invoice" }), _jsx(Typography, { sx: { fontSize: "14px", color: "#667085", lineHeight: "20px", mt: 0.25 }, children: "Additional QuickBooks invoices for this billing month. Tick the ones to map onto the billing summary." })] }), _jsx(IconButton, { onClick: onClose, disabled: loading, sx: { width: 36, height: 36, color: "#98A2B3", "&:hover": { color: "#475467", bgcolor: "#F9FAFB" } }, children: _jsx(X, { size: 20 }) })] }), _jsxs(Box, { sx: { ...ROW_GRID, px: 3, pt: 2, pb: 1 }, children: [_jsx(Checkbox, { className: "table-checkbox", checked: allSelected, indeterminate: selectedCount > 0 && !allSelected, onChange: toggleAll, disabled: loading || fetching || entries.length === 0, sx: CHECKBOX_SX }), _jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#252B37", ...SM_ONLY }, children: "Invoice Date" }), _jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#252B37" }, children: "Client Name" }), _jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#252B37", ...SM_ONLY }, children: "Invoice Amount" }), _jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#252B37" }, children: "Invoice Preview" })] }), _jsx(Box, { sx: { px: 3, pb: 2, minHeight: 140, maxHeight: "52vh", overflowY: "auto" }, children: fetching ? (_jsx(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "center", py: 5 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : entries.length === 0 ? (_jsx(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "center", py: 5 }, children: _jsx(Typography, { sx: { fontSize: "14px", color: "#667085" }, children: "No additional invoices found in QuickBooks for this billing month." }) })) : (entries.map((entry) => (_jsxs(Box, { sx: {
                                    ...ROW_GRID,
                                    py: "10px",
                                    borderTop: "1px solid #F2F4F7",
                                }, children: [_jsx(Checkbox, { className: "table-checkbox", checked: entry.selected, onChange: () => toggleEntry(entry.invoiceId), disabled: loading, sx: CHECKBOX_SX }), _jsx(Typography, { sx: { fontSize: "14px", color: "#27272A", whiteSpace: "nowrap", ...SM_ONLY }, children: entry.invoiceDate || "-" }), _jsx(Typography, { sx: {
                                            fontSize: "14px",
                                            color: "#27272A",
                                            // A long client name shortens rather than widening the column
                                            // and pushing the preview button off the row.
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }, title: entry.client, children: entry.client }), _jsx(Box, { sx: SM_ONLY, children: _jsx(StyledCurrency, { value: formatStat(entry.invoiceAmount, "amount"), fontSize: "14px", valueColor: "#27272a", symbolColor: "#a1a1aa" }) }), _jsxs(AppButton, { variant: "contained", color: "inherit", onClick: () => setPreviewEntry(entry), sx: {
                                            ...BUTTON_SX,
                                            minWidth: 0,
                                            width: "100%",
                                            bgcolor: "#fff",
                                            color: "#414651",
                                            border: "1px solid #D5D7DA",
                                            fontSize: "13px",
                                            gap: 0.75,
                                            "&:hover": { bgcolor: "#F9FAFB", border: "1px solid #D5D7DA" },
                                        }, children: [_jsx("img", { src: "/assets/pdficon.svg", alt: "", style: { width: 18, height: 18 } }), "Preview"] })] }, entry.invoiceId)))) }), error && (_jsx(Box, { sx: { px: 3, pb: 2 }, children: _jsx(Alert, { severity: "error", onClose: () => setError(null), sx: { borderRadius: "10px", alignItems: "center", fontSize: "13px" }, children: error }) })), _jsxs(Box, { sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexWrap: "wrap",
                                px: 3,
                                py: 2,
                                borderTop: "1px solid #EAECF0",
                            }, children: [_jsx(AppButton, { variant: "contained", color: "inherit", disabled: loading, onClick: onClose, sx: {
                                        ...BUTTON_SX,
                                        bgcolor: "#fff",
                                        color: "#414651",
                                        border: "1px solid #D5D7DA",
                                        "&:hover": { bgcolor: "#F9FAFB", border: "1px solid #D5D7DA" },
                                    }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", color: "inherit", 
                                    // Live even with nothing ticked, and even on an empty list once
                                    // something was mapped before: submitting no selection is how every
                                    // mapping is REMOVED. Only a failed read blocks it, since there'd be
                                    // no list to submit.
                                    disabled: loading || fetching, onClick: handleSubmit, sx: {
                                        ...BUTTON_SX,
                                        bgcolor: "#181D27",
                                        color: "#fff",
                                        border: "1px solid #181D27",
                                        "&:hover": { bgcolor: "#101828", border: "1px solid #101828" },
                                    }, children: loading ? _jsx(CustomLoader, { color: "inherit", size: 18 }) : "Save" })] })] }) }), _jsx(InvoicePreviewModal, { open: previewEntry !== null, invoiceId: previewEntry?.invoiceId || "", invoiceNo: previewEntry?.invoiceNo, clientName: previewEntry?.client, onClose: () => setPreviewEntry(null), readOnly: true })] }));
};
export default MappingInvoiceModal;
